import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { ChevronDown } from "lucide-react";
import Button from './Button.js';
import { NavIcon } from './sidebarIcons.js';
import { buildSidebarMenuItems } from '../../utils/sidebarMenu.js';
import companySettlementService from '../../services/companySettlementService.js';
import amountAdjustmentService from '../../services/amountAdjustmentService.js';
import shipmentEditRequestService from '../../services/shipmentEditRequestService.js';
import { useSidebarMobile } from '../../context/SidebarMobileContext.js';
import KeyboardShortcutsTab from './KeyboardShortcutsTab.js';

const PENDING_DOT_PATHS = {
  companySettlementApprovals: '/company-settlement-approvals',
  companySettlements: '/company-settlements',
  amountAdjustments: '/amount-adjustments',
  shipmentEditRequests: '/shipment-edit-requests',
};

const NavPendingDot = () => (
  <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
  </span>
);

const NavLabel = ({ label, showDot }) => (
  <span className="flex items-center gap-2 min-w-0 flex-1">
    <span className="truncate md:truncate-none md:leading-snug">{label}</span>
    {showDot ? <NavPendingDot /> : null}
  </span>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const { isOpen, closeSidebar } = useSidebarMobile();
  const [menuItems, setMenuItems] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [pendingNavDots, setPendingNavDots] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const navRef = useRef(null);

  const pathToNavKey = (path) => path.replace(/\//g, '--');

  useEffect(() => {
    if (user) setMenuItems(buildSidebarMenuItems(user));
  }, [user]);

  const amountDotLoaded = useRef(false);
  const editRequestDotLoaded = useRef(false);
  const dueDotLoaded = useRef(false);

  useEffect(() => {
    if (!user) return undefined;

    const isAgent = user.role === 'Agent';
    const canSeeAmountRequests = ['System Admin', 'Admin', 'Accountant', 'Agent'].includes(user.role);
    const canReviewEditRequests = ['System Admin', 'Admin', 'Accountant'].includes(user.role);
    const shipmentMenuOpen = openDropdown === 'shipments';
    const agentSettleOpen = isAgent && location.pathname.startsWith('/company-settlements');

    if (!shipmentMenuOpen) {
      amountDotLoaded.current = false;
      editRequestDotLoaded.current = false;
    }
    if (!agentSettleOpen) dueDotLoaded.current = false;

    if (shipmentMenuOpen && canSeeAmountRequests && !amountDotLoaded.current) {
      amountDotLoaded.current = true;
      amountAdjustmentService.list({
        status: 'PENDING',
        page: 1,
        per_page: 1,
      }).then((res) => {
        setPendingNavDots((prev) => ({
          ...prev,
          [PENDING_DOT_PATHS.amountAdjustments]: Number(res?.data?.pagination?.total || 0) > 0,
        }));
      }).catch(() => {
        amountDotLoaded.current = false;
      });
    }

    if (shipmentMenuOpen && canReviewEditRequests && !editRequestDotLoaded.current) {
      editRequestDotLoaded.current = true;
      shipmentEditRequestService.list({
        status: 'PENDING',
        page: 1,
        per_page: 1,
      }).then((res) => {
        setPendingNavDots((prev) => ({
          ...prev,
          [PENDING_DOT_PATHS.shipmentEditRequests]: Number(res?.data?.pagination?.total || 0) > 0,
        }));
      }).catch(() => {
        editRequestDotLoaded.current = false;
      });
    }

    if (agentSettleOpen && user.network_mode !== 'EXTERNAL' && !dueDotLoaded.current) {
      dueDotLoaded.current = true;
      companySettlementService.getDueFlag().then((sumRes) => {
        setPendingNavDots((prev) => ({
          ...prev,
          [PENDING_DOT_PATHS.companySettlements]: Boolean(sumRes?.data?.has_due),
        }));
      }).catch(() => {
        dueDotLoaded.current = false;
      });
    }
  }, [user, openDropdown, location.pathname]);

  useEffect(() => {
    if (user && isOpen) {
      const activeDropdown = menuItems.find(item => {
        if (item.type === 'dropdown') {
          return item.children?.some(child => child.visible && isActive(child.path));
        }
        return false;
      });

      if (activeDropdown && openDropdown !== activeDropdown.id) {
        setOpenDropdown(activeDropdown.id);
      }
    }
  }, [location.pathname, menuItems, user, isOpen]);

  const isActive = (path) => location.pathname.startsWith(path);

  const findActiveNavTargetId = useCallback(() => {
    let bestId = null;
    let bestLength = 0;

    menuItems.forEach((item) => {
      if (item.type === 'single' && item.path && isActive(item.path)) {
        if (item.path.length >= bestLength) {
          bestLength = item.path.length;
          bestId = `single-${item.id}`;
        }
      }
      if (item.type === 'dropdown') {
        const visibleChildren = (item.children || []).filter((child) => child.visible && child.path);
        if (visibleChildren.length === 1) {
          const only = visibleChildren[0];
          if (isActive(only.path) && only.path.length >= bestLength) {
            bestLength = only.path.length;
            bestId = `single-${item.id}`;
          }
          return;
        }
        visibleChildren.forEach((child) => {
          if (isActive(child.path) && child.path.length >= bestLength) {
            bestLength = child.path.length;
            bestId = `child-${pathToNavKey(child.path)}`;
          }
        });
      }
    });

    return bestId;
  }, [menuItems, location.pathname]);

  useEffect(() => {
    if (!isOpen || window.innerWidth >= 1024 || menuItems.length === 0) return undefined;

    const scrollToActiveItem = () => {
      const navEl = navRef.current;
      if (!navEl) return;

      const targetId = findActiveNavTargetId();
      if (!targetId) return;

      const activeEl = navEl.querySelector(`[data-nav-id="${targetId}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    const timer = window.setTimeout(scrollToActiveItem, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen, openDropdown, menuItems, location.pathname, findActiveNavTargetId]);

  const isDropdownActive = (children) => {
    return children?.some(child => child.visible && isActive(child.path));
  };

  const hasPendingDot = (path) => Boolean(path && pendingNavDots[path]);

  const dropdownHasPendingDot = (children) =>
    children?.some((child) => child.visible && hasPendingDot(child.path));

  const isMobileView = () => window.innerWidth < 1024;

  const handleNavigation = (path, { closeOnMobile = true } = {}) => {
    navigate(path);
    if (isMobileView() && closeOnMobile) {
      closeSidebar();
    }
  };

  const toggleDropdown = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  const handleDropdownLabelClick = (item) => {
    toggleDropdown(item.id);
  };

  const handleChevronClick = (event, itemId) => {
    event.stopPropagation();
    toggleDropdown(itemId);
  };

  const handleChildNavigation = (path) => {
    handleNavigation(path);
  };

  if (loading || !user) {
    return (
      <>
        <aside className="hidden lg:flex w-[300px] bg-white border-r border-gray-200 h-full flex-col pt-4 shrink-0 overflow-hidden">
          <div className="px-4 space-y-3">
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse w-3/4" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse w-2/3" />
          </div>
        </aside>
      </>
    );
  }

  return (
    <>
      {/* Mobile overlay — no backdrop-blur so header stays sharp */}
      {isOpen && (
        <div
          className="absolute lg:hidden inset-0 bg-black/40 z-[60]"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          absolute lg:relative inset-y-0 left-0 z-[70] lg:z-auto
          h-full w-[280px] md:w-[300px] bg-white border-r border-gray-200
          flex flex-col shrink-0 overflow-hidden
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isOpen ? 'lg:shadow-none shadow-xl' : ''}
        `}
      >
        {/* Nav items */}
        <nav ref={navRef} className="sidebar-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3 px-3 space-y-1 bg-white">
          {menuItems.map((item) => {
            const itemLabel = t(item.labelKey);
            if (item.type === 'single') {
              const active = isActive(item.path);
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  data-nav-id={`single-${item.id}`}
                  onClick={() => handleNavigation(item.path)}
                  className={`group w-full justify-start gap-3 px-3 py-2.5 rounded-lg text-sm md:whitespace-normal ${
                    active
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={itemLabel}
                >
                  <NavIcon labelKey={item.labelKey} active={active} />
                  <span className="truncate md:truncate-none md:leading-snug">{itemLabel}</span>
                </Button>
              );
            }

            if (item.type === 'dropdown') {
              const visibleChildren = (item.children || []).filter((child) => child.visible && child.path);
              if (visibleChildren.length === 1) {
                const only = visibleChildren[0];
                const active = isActive(only.path);
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    data-nav-id={`single-${item.id}`}
                    onClick={() => handleNavigation(only.path)}
                    className={`group w-full justify-start gap-3 px-3 py-2.5 rounded-lg text-sm md:whitespace-normal ${
                      active
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={itemLabel}
                  >
                    <NavIcon labelKey={item.labelKey} active={active} />
                    <NavLabel label={itemLabel} showDot={hasPendingDot(only.path)} />
                  </Button>
                );
              }

              const dropdownActive = isDropdownActive(item.children);
              const isDropdownOpen = openDropdown === item.id;

              return (
                <div key={item.id}>
                  <div
                    className={`group flex items-stretch rounded-lg transition-colors duration-200 ${
                      dropdownActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Button
                      variant="ghost"
                      onClick={() => handleDropdownLabelClick(item)}
                      className={`flex-1 min-w-0 md:min-w-fit justify-start gap-3 px-3 py-2.5 rounded-lg rounded-r-none !bg-transparent hover:!bg-transparent active:!bg-transparent text-sm md:whitespace-normal ${
                        dropdownActive
                          ? 'text-blue-700 font-semibold'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`}
                      title={itemLabel}
                    >
                      <NavIcon labelKey={item.labelKey} active={dropdownActive} />
                      <NavLabel label={itemLabel} showDot={dropdownHasPendingDot(item.children)} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={(event) => handleChevronClick(event, item.id)}
                      className={`shrink-0 px-2 py-2.5 rounded-lg rounded-l-none !bg-transparent hover:!bg-transparent active:!bg-transparent ${
                        dropdownActive
                          ? 'text-blue-700'
                          : 'text-gray-500 group-hover:text-gray-800'
                      }`}
                      aria-label={isDropdownOpen ? 'Collapse section' : 'Expand section'}
                      aria-expanded={isDropdownOpen}
                    >
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </Button>
                  </div>

                  {isDropdownOpen && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3 bg-white">
                      {item.children.filter(child => child.visible).map((child) => {
                        const childActive = isActive(child.path);
                        const childLabel = child.label || t(child.labelKey);
                        return (
                          <Button
                            key={child.path}
                            variant="ghost"
                            data-nav-id={`child-${pathToNavKey(child.path)}`}
                            onClick={() => handleChildNavigation(child.path)}
                            className={`group w-full justify-start gap-2 px-2 py-2 rounded-md text-sm md:whitespace-normal ${
                              childActive
                                ? 'text-blue-700 font-semibold bg-blue-50/50'
                                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                          >
                            <NavIcon labelKey={child.labelKey} size="sm" active={childActive} />
                            <NavLabel label={childLabel} showDot={hasPendingDot(child.path)} />
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </nav>
        <KeyboardShortcutsTab active={location.pathname.startsWith('/keyboard-shortcuts')} />
      </aside>
    </>
  );
};

export default Sidebar;
